import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';
import {
  selectPastTransactions,
  selectPastTransactionsLoading,
  selectPastTransactionsStatus,
  selectPastTransactionsTotal,
  selectPastTransactionsNextOffset,
} from 'store/transactions/selectors';
import { TransactionDetails } from 'store/transactions/types';
import { getPastTransactions } from 'store/transactions/actions';
import { ACTION_STATUSES, BOOKINGS_PAGE_SIZE } from 'shared/consts';

/* One frozen empty array, so "not fetched yet" stays referentially stable across renders. */
const NO_ROWS: Array<TransactionDetails> = [];

/**
 * Shared "has it ended" test for the surfaces that withhold a cancel on a finished booking. The end
 * itself, with no grace: helix answers a cancel from `end_at` onwards with "The booking has already
 * ended", while its Upcoming list keeps the booking for another thirty minutes. The list window
 * decides which tab a booking appears on -- it is not permission to act on it.
 */
export const hasBookingEnded = (endAt?: string | Date): boolean => !!endAt && new Date(endAt).getTime() < Date.now();

/** A slot that has started can no longer be booked again, so a retry stops being on offer. */
export const hasBookingStarted = (startAt?: string | Date): boolean =>
  !!startAt && new Date(startAt).getTime() <= Date.now();

/** Non-fetching twin of `usePastBookings`'s `isPastListEmpty`. If the page used the fetching hook
 * as well, both would dispatch the first read in the same tick and the tab would read twice. */
export const useIsPastBookingsListEmpty = (): boolean => {
  const pastTransactions = useSelector(selectPastTransactions);
  const pastStatus = useSelector(selectPastTransactionsStatus);

  return !pastTransactions?.length && pastStatus === ACTION_STATUSES.FULFILLED;
};

interface UsePastBookingsProps {
  /** Lazy: nothing is fetched until the Past tab is selected. */
  enabled: boolean;
}

interface UsePastBookingsReturnValues {
  pastBookings: Array<TransactionDetails>;
  isPastBookingsLoading: boolean;
  /** Settled on having nothing to show -- false until the read comes back, so no flash of empty. */
  isPastListEmpty: boolean;
  hasMore: boolean;
  /** A page landing on top of rows already shown, as opposed to the first paint's skeleton. */
  isLoadingMore: boolean;
  /** Ask for the next page. Safe to call on every scroll frame; it no-ops unless one is due. */
  loadMore: () => void;
}

export const usePastBookings = ({ enabled }: UsePastBookingsProps): UsePastBookingsReturnValues => {
  const dispatch = useDispatch();
  const pastTransactions = useSelector(selectPastTransactions);
  const isPastBookingsLoading = useSelector(selectPastTransactionsLoading);
  const pastStatus = useSelector(selectPastTransactionsStatus);
  const total = useSelector(selectPastTransactionsTotal);
  const hasRequestedFirstPageRef = useRef(false);

  // Rows as helix returned them (`is_upcoming=false`, ordered `end_at desc`). Filtering or re-sorting
  // here leaves holes in a page `total` still counted, and scrambles rows against the next `offset`.
  const pastBookings = pastTransactions ?? NO_ROWS;

  // Not `pastBookings.length` -- see `transactionsNextOffset` in store/transactions/types.
  const nextOffset = useSelector(selectPastTransactionsNextOffset);
  const hasMore = total !== null && nextOffset < total;

  // The epic is a `switchMap`, so a second request cancels the one in flight: the loading guard is
  // what stops a per-scroll-frame call from discarding the page it just asked for.
  const loadMore = useCallback(() => {
    if (isPastBookingsLoading || !hasMore) {
      return;
    }

    dispatch(getPastTransactions.request({ offset: nextOffset, limit: BOOKINGS_PAGE_SIZE }));
  }, [dispatch, hasMore, isPastBookingsLoading, nextOffset]);

  // The ref, not the status, is the guard: a REJECTED read is neither PENDING nor FULFILLED and
  // leaves the list empty, so a status-based condition fires again the moment it fails. This effect
  // owns the first page only; `loadMore` dispatches the same action for every page after it.
  useEffect(() => {
    if (
      !enabled ||
      hasRequestedFirstPageRef.current ||
      pastStatus === ACTION_STATUSES.PENDING ||
      pastTransactions?.length
    ) {
      return;
    }

    hasRequestedFirstPageRef.current = true;
    dispatch(getPastTransactions.request({ offset: 0, limit: BOOKINGS_PAGE_SIZE }));
  }, [dispatch, enabled, pastStatus, pastTransactions]);

  return {
    pastBookings,
    isPastBookingsLoading,
    isPastListEmpty: !pastBookings.length && pastStatus === ACTION_STATUSES.FULFILLED,
    hasMore,
    isLoadingMore: isPastBookingsLoading && pastBookings.length > 0,
    loadMore,
  };
};
